/**
 * 2D overlay for rendering keypoint markers with labels.
 *
 * Displays a small circle at the keypoint's projected screen position
 * with a text label (e.g., "P2") offset to the side.
 */

import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface KeypointOverlayConfig {
    readonly id: string;
    readonly label: string;
    readonly worldPosition: Vec3;
    readonly color?: string;
}

export class KeypointOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 200;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private label: string;
    worldPosition: Vec3;
    canvasPosition = { x: 0, y: 0 };
    private color: string;

    private readonly labelOffset = { x: 8, y: -8 };
    private readonly font = "14px 'JetBrains Mono', monospace";

    constructor(config: KeypointOverlayConfig) {
        this.id = config.id;
        this.label = config.label;
        this.worldPosition = config.worldPosition;
        this.color = config.color ?? '#00ccff';
    }

    setWorldPosition(position: Vec3): void {
        this.worldPosition = position;
    }

    hitTest(_canvasX: number, _canvasY: number): boolean {
        return false;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        const projected = viewport.worldToCanvas(this.worldPosition);
        if (!projected.visible) return;

        this.canvasPosition = { x: projected.x, y: projected.y };
        const { x, y } = this.canvasPosition;

        // Label
        ctx.save();
        ctx.font = this.font;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        const labelX = x + this.labelOffset.x;
        const labelY = y + this.labelOffset.y;
        const text = this.label;

        // Label text
        ctx.fillStyle = this.color;
        ctx.fillText(text, labelX, labelY);

        ctx.restore();
    }
}
